export type Corner = 'tl' | 'tr' | 'br' | 'bl';

type Point = [number, number];
type CornerPoints = [Point, Point, Point, Point];

export type CornerBracketResult = {
  corner: Corner;
  path: string;
  outer: CornerPoints;
  inner: CornerPoints;
  gradientAngle: number;
  gradientCenter: Point;
};

/**
 * Generates the main beveled rectangle path for use with CSS clip-path.
 * When inset > 0, the path is centered within the original bounds.
 */
export function getFramePath(
  width: number,
  height: number,
  bevel: number,
  inset = 0,
  sharpCorners: Corner[] = [],
): string {
  const o = inset / 2; // offset from edges
  const w = width - o; // right edge
  const h = height - o; // bottom edge

  // Per-corner bevel: 0 if sharp, else adjusted bevel
  const baseBevel = Math.max(0, bevel - o);
  const bTl = sharpCorners.includes('tl') ? 0 : baseBevel;
  const bTr = sharpCorners.includes('tr') ? 0 : baseBevel;
  const bBr = sharpCorners.includes('br') ? 0 : baseBevel;
  const bBl = sharpCorners.includes('bl') ? 0 : baseBevel;

  return [
    `M ${o + bTl} ${o}`,
    `L ${w - bTr} ${o}`,
    `L ${w} ${o + bTr}`,
    `L ${w} ${h - bBr}`,
    `L ${w - bBr} ${h}`,
    `L ${o + bBl} ${h}`,
    `L ${o} ${h - bBl}`,
    `L ${o} ${o + bTl}`,
    'Z',
  ].join(' ');
}

/**
 * Generates a stroke-only path by combining outer and inner frame paths.
 * Uses evenodd fill rule - the area between the two paths becomes the visible stroke.
 */
export function getFrameStrokePath(
  width: number,
  height: number,
  bevel: number,
  strokeWidth: number,
  sharpCorners: Corner[] = [],
): string {
  const outerPath = getFramePath(width, height, bevel, 0, sharpCorners);
  const innerPath = getFramePath(width, height, bevel, strokeWidth * 2, sharpCorners);
  return `${outerPath} ${innerPath}`;
}

export function getInverseClipPath(width: number, height: number, framePath: string, padding = 20): string {
  // Large rectangle that covers element + padding for blur overflow
  const outerRect = `M ${-padding} ${-padding} L ${width + padding}         
  ${-padding} L ${width + padding} ${height + padding} L ${-padding} ${height + padding} Z`;
  return `${outerRect} ${framePath}`;
}

/**
 * Generates corner bracket path for use with CSS clip-path.
 */
export function getCornerBracket(
  width: number,
  height: number,
  bevel: number,
  thickness: number,
  legLength: number,
  corner: Corner,
  legMul = 0.25,
): CornerBracketResult {
  const t = thickness;
  const b = bevel;
  const leg = legLength;

  const coords: Record<Corner, Omit<CornerBracketResult, 'corner' | 'path'>> = {
    tl: {
      outer: [
        [0, b + leg * legMul],
        [0, b],
        [b, 0],
        [b + leg, 0],
      ],
      inner: [
        [b + leg, t],
        [b + t * 0.7, t],
        [t, b + t * 0.7],
        [t, b + leg * legMul],
      ],
      gradientAngle: 225,
      gradientCenter: [0, 0],
    },
    tr: {
      outer: [
        [width - b - leg, 0],
        [width - b, 0],
        [width, b],
        [width, b + leg * legMul],
      ],
      inner: [
        [width - t, b + leg * legMul],
        [width - t, b + t * 0.7],
        [width - b - t * 0.7, t],
        [width - b - leg, t],
      ],
      gradientAngle: 315,
      gradientCenter: [width, 0],
    },
    br: {
      outer: [
        [width, height - b - leg * legMul],
        [width, height - b],
        [width - b, height],
        [width - b - leg, height],
      ],
      inner: [
        [width - b - leg, height - t],
        [width - b - t * 0.7, height - t],
        [width - t, height - b - t * 0.7],
        [width - t, height - b - leg * legMul],
      ],
      gradientAngle: 45,
      gradientCenter: [width, height],
    },
    bl: {
      outer: [
        [b + leg, height],
        [b, height],
        [0, height - b],
        [0, height - b - leg * legMul],
      ],
      inner: [
        [t, height - b - leg * legMul],
        [t, height - b - t * 0.7],
        [b + t * 0.7, height - t],
        [b + leg, height - t],
      ],
      gradientAngle: 135,
      gradientCenter: [0, height],
    },
  };

  const c = coords[corner];
  const [outerStart, ...outerRest] = c.outer;
  const [innerStart, ...innerRest] = c.inner;

  const path = [
    `M ${outerStart[0]} ${outerStart[1]}`,
    ...outerRest.map(([x, y]) => `L ${x} ${y}`),
    `L ${innerStart[0]} ${innerStart[1]}`,
    ...innerRest.map(([x, y]) => `L ${x} ${y}`),
    'Z',
  ].join(' ');

  return { corner, path, ...c };
}
